import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

// Services
import { MessageDataService } from '../../../services/message-details/message-details.service';
import { SelectedMessageDetailService } from '../../../services/selected-message-detail/selected-message-detail.service';
import { ResponsiveBreakpointService } from '../../../services/breakpoint/responsive-breakpoint.service';

// Models
import {
  StatementMessageModel,
} from '../../../core/interfaces/statement-messaging-run.model';

// Types
import { RadioState } from '@vg-constellation/angular-19/radio';

/**
 * Type alias for message table rows
 */
type MessageTableRow = StatementMessageModel;

@Component({
  selector: 'app-message-detail-table',
  templateUrl: './message-detail-table.component.html',
  styleUrl: './message-detail-table.component.scss',
  standalone: false,
})
export class MessageDetailTableComponent implements OnInit, OnDestroy {
  // ============================================
  // Constants
  // ============================================
  private readonly DEFAULT_ITEMS_PER_PAGE = 5;
  private readonly INITIAL_PAGE = 1;
  protected readonly MESSAGE_TYPE_LIMIT = 37;

  // ============================================
  // Public Properties
  // ============================================
  data: MessageTableRow[] = [];
  paginatingTableDataSlice: MessageTableRow[] = [];
  itemsPerPage = this.DEFAULT_ITEMS_PER_PAGE;
  currentPage = this.INITIAL_PAGE;
  errorMessage = '';
  isLoading = false;
  isFilteredByRunId = false;

  // ============================================
  // Services
  // ============================================
  readonly responsiveService = inject(ResponsiveBreakpointService);
  private readonly messageDataService = inject(MessageDataService);
  private readonly selectedMessageService = inject(SelectedMessageDetailService);
  private readonly destroy$ = new Subject<void>();

  // ============================================
  // Lifecycle Hooks
  // ============================================

  ngOnInit(): void {
    this.subscribeToMessageData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // Subscriptions
  // ============================================

  /**
   * Subscribe to message data changes from service
   */
  private subscribeToMessageData(): void {
    this.messageDataService.messageData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.updateComponentState(state);
        this.resetToFirstPage();
        this.updatePaginationData();
      });
  }

  /**
   * Update component state from service state
   */
  private updateComponentState(state: any): void {
    this.data = state.data.statementMessages;
    this.errorMessage = state.errorMessage;
    this.isLoading = state.isLoading;
    this.isFilteredByRunId = state.filteredByRunId;
  }

  /**
   * Reset current page to first page
   */
  private resetToFirstPage(): void {
    this.currentPage = this.INITIAL_PAGE;
  }

  // ============================================
  // Pagination Methods
  // ============================================

  /**
   * Update pagination data slice
   */
  private updatePaginationData(): void {
    if (this.hasNoData()) {
      this.clearPaginationSlice();
      return;
    }

    this.paginatingTableDataSlice = this.calculatePaginationSlice();
  }

  /**
   * Check if data array is empty
   */
  private hasNoData(): boolean {
    return !this.data || this.data.length === 0;
  }

  /**
   * Clear pagination slice
   */
  private clearPaginationSlice(): void {
    this.paginatingTableDataSlice = [];
  }

  /**
   * Calculate current page data slice
   */
  private calculatePaginationSlice(): MessageTableRow[] {
    const start = this.getStartIndex();
    const end = this.getEndIndex(start);
    return this.data.slice(start, end);
  }

  /**
   * Get start index for current page
   */
  private getStartIndex(): number {
    return (this.currentPage - 1) * this.itemsPerPage;
  }

  /**
   * Get end index for current page
   */
  private getEndIndex(startIndex: number): number {
    return startIndex + this.itemsPerPage;
  }

  /**
   * Handle page change event
   */
  handlePageChange(activePage: number): void {
    if (this.isInvalidPageChange(activePage)) {
      return;
    }

    this.currentPage = activePage;
    this.updatePaginationData();
  }

  /**
   * Check if page change is invalid
   */
  private isInvalidPageChange(newPage: number): boolean {
    return this.isCurrentPage(newPage) || this.isInvalidPage(newPage);
  }

  /**
   * Check if already on requested page
   */
  private isCurrentPage(page: number): boolean {
    return this.currentPage === page;
  }

  /**
   * Check if page number is invalid
   */
  private isInvalidPage(page: number): boolean {
    return page < 1 || page > this.totalPages;
  }

  // ============================================
  // Selection Methods
  // ============================================

  /**
   * Handle message detail selection
   */
  messageDetailSelection(_stateChange: RadioState, messageDetail: MessageTableRow): void {
    this.selectedMessageService.selectMessage(messageDetail);
  }

  // ============================================
  // Error Handling Methods
  // ============================================

  /**
   * Clear error message and update service state
   */
  clearErrorMessage(): void {
    this.errorMessage = '';
    this.messageDataService.updateState({ errorMessage: '' });
  }

  // ============================================
  // Computed Properties
  // ============================================

  /**
   * Get total page count
   */
  get totalPages(): number {
    if (this.hasNoData()) {
      return 0;
    }
    return Math.ceil(this.data.length / this.itemsPerPage);
  }

  /**
   * Check if has data
   */
  get hasData(): boolean {
    return this.data.length > 0;
  }

  /**
   * Check if has error
   */
  get hasError(): boolean {
    return !!this.errorMessage;
  }

  /**
   * Get current data range display text
   */
  get dataRangeText(): string {
    if (this.hasNoData()) {
      return 'No items';
    }

    const start = this.getStartIndex() + 1;
    const end = Math.min(this.getEndIndex(start - 1), this.data.length);
    return `${start}-${end} of ${this.data.length}`;
  }
}
